import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Button, Container, Row, Col, Table, Card, Modal, InputGroup, Tab, Nav, Badge, Accordion } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { getCorrieri, getBanche, getClienti, createOrdine, getOrdine, createFilm as createFilmAPI, searchItems, getUbicazioniProdotto } from '../../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { METODI_PAGAMENTO, bancaSuggerita } from '../../common/metodiPagamento';

const NuovoOrdine = () => {
  // Mobile responsive hook
  const isMobile = useMediaQuery({ maxWidth: 991 });
  
  // State for dropdowns and suggestions
  const [corrieri, setCorrieri] = useState([]);
  const [allClienti, setAllClienti] = useState([]); // All clients for dropdown
  const [banche, setBanche] = useState([]);
  const [searchResults, setSearchResults] = useState([]); // New state for unified search results

  // Main order state
  const [ordine, setOrdine] = useState({
    cliente_id: '',
    corriere_id: '',
    banca_id: '',
    payment_method: '',
    data_ordine: new Date().toLocaleDateString('sv-SE'),
    spese_trasporto: 0,
    spese_incasso_percentuale: 0,
    acconto: 0,
    sconto: 0,
    tipo_sconto: 'fisso', // New field for discount type
    origine: '',
    numero_ordine_marketplace: '',
    invia_email_cliente: false,
    note: '',
    dettagli: [],
    subtotale_imponibile: 0,
    totale_iva: 0,
    totale_finale: 0,
    stato: 'Da Evadere',
    costo_prodotti: 0,
    costo_marketplace: 0,
    costo_reale_corriere: 0,
    margine_lordo: 0,
    margine: 0
  });

  // Origini in cui è ammesso quantità=0 per film prenotabili (ordine su richiesta).
  // IBS/FELTRINELLI escluse: i marketplace non pubblicano offerte con stock 0.
  const ORIGINI_QT_ZERO_AMMESSE = ['NEGOZIO', 'SITO WEB', 'ECOMMERCE'];

  // Per queste origini la mail di conferma al cliente non parte da sola: si spunta la casella.
  const EMAIL_FACOLTATIVA = ['NEGOZIO', 'RIVERSAMENTI', 'ALTRO'];
  const casellaEmailCliente = (
    <Form.Check
      type="checkbox"
      id="invia-email-cliente"
      label="Invia al cliente l'email di conferma ordine"
      checked={!!ordine.invia_email_cliente}
      onChange={(e) => setOrdine(prev => ({ ...prev, invia_email_cliente: e.target.checked }))}
    />
  );

  // State for the new line item form
  const [selectedItem, setSelectedItem] = useState(null); // Changed from selectedFilm to selectedItem
  const [quantita, setQuantita] = useState(1);
  const [ubicazioniProdotto, setUbicazioniProdotto] = useState([]); // Righe {ubicazione, quantita} del prodotto selezionato
  const [ubicazioneScelta, setUbicazioneScelta] = useState('');
  const [isCustomProductMode, setIsCustomProductMode] = useState(false); // New state for custom product mode
  const [customProductDescription, setCustomProductDescription] = useState('');
  const [customProductPrice, setCustomProductPrice] = useState(0);
  const [customProductCost, setCustomProductCost] = useState(0); // New state for custom product cost
  const [customProductIva, setCustomProductIva] = useState(22); // Default IVA
  const [editingDetail, setEditingDetail] = useState(null); // State per gestire la riga in modifica

  // State for new cliente modal
  // const [showClienteModal, setShowClienteModal] = useState(false);
  // const [nuovoCliente, setNuovoCliente] = useState({ nome: '', cognome: '', denominazione_sociale: '' });

  // State for new film modal (will be used for generic product creation if needed, or removed)
  const [showFilmModal, setShowFilmModal] = useState(false);
  const [nuovoFilm, setNuovoFilm] = useState({ titolo: '' });

  const location = useLocation();
  const navigate = useNavigate();

  const mapOrigineToCanale = (origine) => {
    switch (origine) {
      case 'NEGOZIO':
        return 'NEGOZIO';
      case 'SITO_WEB':
      case 'SITO WEB': // Supporto retrocompatibilità
        return 'SITO_WEB';
      case 'IBS':
        return 'IBS';
      case 'FELTRINELLI':
        return 'FELTRINELLI';
      case 'RIVERSAMENTI':
        return 'RIVERSAMENTI';
      // Aggiungere altri casi se necessario
      default:
        return null; // Nessun canale specifico o fallback
    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [corrieriRes, bancheRes, clientiRes] = await Promise.all([
            getCorrieri(),
            getBanche(),
            getClienti(0, 100000, ['cognome:asc'])
        ]);
        setCorrieri(corrieriRes.data.corrieri);
        
        if (clientiRes && clientiRes.data && Array.isArray(clientiRes.data.clienti)) {
            setAllClienti(clientiRes.data.clienti);
        
        } else {
            console.error("Errore: la risposta dell'API per i clienti non è nel formato previsto.", clientiRes);
            setAllClienti([]);
        }

        setBanche(bancheRes.data.banche);
        
      } catch (error) {
        console.error("Errore nel caricamento dei dati iniziali", error);
        if (error.response) {
          console.error("Dati dell'errore:", error.response.data);
          console.error("Stato dell'errore:", error.response.status);
          console.error("Header dell'errore:", error.response.headers);
        } else if (error.request) {
          console.error("Richiesta dell'errore:", error.request);
        } else {
          console.error('Errore', error.message);
        }
      }
    };
    fetchData();
  }, []);

  // Duplication logic
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const duplicateId = queryParams.get('duplicateId');

    if (duplicateId) {
      const fetchOrdineToDuplicate = async () => {
        try {
          const response = await getOrdine(parseInt(duplicateId));
          const duplicatedOrdine = response.data;

          // Clear ID for duplication
          setOrdine({
            ...duplicatedOrdine,
            id: undefined, // Ensure ID is not sent for new creation
            data_ordine: duplicatedOrdine.data_ordine.split('T')[0], // Format date for input type="date"
            // Details might need adjustment if they contain IDs that should be cleared
            dettagli: duplicatedOrdine.dettagli.map(detail => {
                let titolo = '';
                let prezzo_unitario_display = detail.prezzo_unitario;
                let iva_percentuale_display = detail.iva_percentuale;

                if (detail.prodotto_libero) {
                    titolo = detail.descrizione_prodotto_libero;
                    prezzo_unitario_display = detail.prezzo_unitario_libero;
                    iva_percentuale_display = detail.iva_percentuale_libero;
                } else if (detail.film) {
                    titolo = detail.film.titolo;
                } else if (detail.musica) {
                    titolo = detail.musica.titolo;
                } else if (detail.servizio) {
                    titolo = detail.servizio.titolo_servizio;
                }
                return {
                    ...detail,
                    id: undefined, // Clear detail IDs as well
                    titolo: titolo,
                    prezzo_unitario: prezzo_unitario_display,
                    iva_percentuale: iva_percentuale_display
                };
            })
          });
          // Optionally remove duplicateId from URL after processing
          navigate(location.pathname, { replace: true });
        } catch (error) {
          console.error("Errore durante il recupero dell'ordine per la duplicazione:", error);
          alert("Errore durante il recupero dei dati per la duplicazione.");
        }
      };
      fetchOrdineToDuplicate();
    }
  }, [location.search, navigate, location.pathname]);

  // Effect to pre-select client if passed from new client creation
  useEffect(() => {
      if (location.state && location.state.newClientId) {
          const newClientId = location.state.newClientId;
          setOrdine(prevOrdine => ({ ...prevOrdine, cliente_id: newClientId }));
          // Rimuovi lo stato per evitare pre-selezione indesiderata in futuro
          navigate(location.pathname, { replace: true, state: {} }); 
      }
  }, [location.state, navigate, location.pathname]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === 'sconto') { // Only 'sconto' uses this handleChange among numeric fields
        finalValue = value === '' ? 0 : parseFloat(value);
        if (isNaN(finalValue)) {
            finalValue = 0;
        }
    } else if (name === 'tipo_sconto') {
        finalValue = value; // Keep as string for enum
    } else if (name === 'data_ordine') { // Also uses handleChange for date
        finalValue = value;
    }

    setOrdine(prevOrdine => ({
      ...prevOrdine,
      [name]: finalValue,
    }));
  }, []);
  const handleBancaChange = useCallback((e) => {
    const bancaId = e.target.value;
    const selectedBanca = banche.find(b => b.id === parseInt(bancaId));
    setOrdine(prev => ({
      ...prev,
      banca_id: bancaId ? parseInt(bancaId) : null,
      spese_incasso_percentuale: selectedBanca ? selectedBanca.costo_transazione_percentuale || 0 : 0
    }));
  }, [banche]);

  // Il metodo suggerisce la banca solo se non e' ancora stata scelta
  const handlePaymentMethodChange = useCallback((e) => {
    const metodo = e.target.value;
    setOrdine(prev => {
      const next = { ...prev, payment_method: metodo };
      if (!prev.banca_id) {
        const suggerita = bancaSuggerita(metodo, banche);
        if (suggerita) {
          next.banca_id = suggerita.id;
          next.spese_incasso_percentuale = suggerita.costo_transazione_percentuale || 0;
        }
      }
      return next;
    });
  }, [banche]);

  const handleClienteChange = useCallback((selected) => {
    if (selected.length > 0) {
      const cliente = selected[0];
      const scontoCliente = cliente.sconto_percentuale && cliente.sconto_percentuale > 0
        ? { sconto: cliente.sconto_percentuale, tipo_sconto: 'percentuale' }
        : {};
      setOrdine(prevOrdine => ({ ...prevOrdine, cliente_id: cliente.id, ...scontoCliente }));
    } else {
      setOrdine(prevOrdine => ({ ...prevOrdine, cliente_id: '' }));
    }
  }, []);

  const handleSearch = useCallback(async (searchText) => { // Rinominato query in searchText per chiarezza
      if (searchText.length > 2) {
          try {
              const response = await searchItems({ 
                  query: searchText, 
                  sku: searchText, 
                  barcode: searchText 
              });
              setSearchResults(response.data);
          } catch (error) {
              console.error("Errore durante la ricerca unificata:", error);
              setSearchResults([]);
          }
      } else {
          setSearchResults([]);
      }
  }, []);

  // Recupera il breakdown per ubicazione del prodotto selezionato (Film/Musica), per poter
  // chiedere all'operatore da quale locazione fisica sta prelevando il pezzo se ce n'è più di una.
  useEffect(() => {
    const tipo = selectedItem?.item_type;
    if (!selectedItem || (tipo !== 'Film' && tipo !== 'Musica')) {
      setUbicazioniProdotto([]);
      setUbicazioneScelta('');
      return;
    }
    let cancelled = false;
    getUbicazioniProdotto(tipo, selectedItem.id)
      .then(res => {
        if (cancelled) return;
        const righe = (res.data?.righe || []).filter(r => r.quantita > 0);
        setUbicazioniProdotto(righe);
        setUbicazioneScelta(righe.length === 1 ? righe[0].ubicazione : '');
      })
      .catch(() => {
        if (!cancelled) {
          setUbicazioniProdotto([]);
          setUbicazioneScelta('');
        }
      });
    return () => { cancelled = true; };
  }, [selectedItem]);

  // General change handler for order header
  const handleOrdineChange = useCallback((e) => {
    const { name, value } = e.target;
    setOrdine(prev => ({ ...prev, [name]: value }));
  }, []);

  // Add/Remove line items
  const addLineItem = () => {
    if (isCustomProductMode) {
        if (!customProductDescription || quantita <= 0 || customProductPrice <= 0) {
            alert("Inserisci descrizione, quantità e prezzo validi per il prodotto personalizzato.");
            return;
        }
        const item = {
            prodotto_libero: true,
            descrizione_prodotto_libero: customProductDescription,
            prezzo_unitario_libero: parseFloat(customProductPrice),
            costo_unitario_libero: parseFloat(customProductCost), // Include custom product cost
            iva_percentuale_libero: parseFloat(customProductIva),
            quantita: parseInt(quantita),
            // For display purposes in the table
            titolo: customProductDescription,
            prezzo_unitario: parseFloat(customProductPrice),
            iva_percentuale: parseFloat(customProductIva)
        };
        setOrdine(prev => ({ ...prev, dettagli: [...prev.dettagli, item] }));
        setCustomProductDescription('');
        setCustomProductPrice(0);
        setCustomProductCost(0); // Reset custom product cost
        setCustomProductIva(22);
        setQuantita(1);
    } else {
        if (!selectedItem) { // selectedItem ora rappresenta l'elemento generico selezionato
            alert("Seleziona un prodotto o servizio.");
            return;
        }
        if (quantita < 0) {
            alert("La quantità non può essere negativa.");
            return;
        }

        // Warn if quantity exceeds available stock for Film/Musica
        if ((selectedItem.item_type === "Film" || selectedItem.item_type === "Musica") && quantita > selectedItem.quantita) {
            if (!window.confirm(`Attenzione: La quantità richiesta (${quantita}) supera la disponibilità (${selectedItem.quantita}) per ${selectedItem.titolo}. Continuare?`)) {
                return; // User cancelled
            }
        }

        // Se il prodotto ha più di un'ubicazione con stock, l'operatore deve scegliere da dove lo preleva
        if (ubicazioniProdotto.length > 1 && !ubicazioneScelta) {
            alert("Seleziona da quale ubicazione stai prelevando il prodotto.");
            return;
        }

        const { origine } = ordine;
        const isMarketplace = origine === 'IBS' || origine === 'FELTRINELLI';

        let prezzoUnitario;
        if (isMarketplace && (selectedItem.item_type === 'Film' || selectedItem.item_type === 'Musica') && selectedItem.prezzo_ibs != null) {
            prezzoUnitario = selectedItem.prezzo_ibs;
        } else {
            // Fallback to standard price for non-marketplace orders or if prezzo_ibs is not available
            prezzoUnitario = selectedItem.prezzo_negozio || selectedItem.prezzo || 0;
        }

        let itemToAdd = {
            quantita: parseInt(quantita),
            prezzo_unitario: prezzoUnitario,
            iva_percentuale: selectedItem.iva || 22,
            costo_acquisto_unitario: selectedItem.costo_acquisto || selectedItem.costo || 0,
            titolo: selectedItem.titolo,
            item_type: selectedItem.item_type,
            film_id: selectedItem.item_type === "Film" ? selectedItem.id : null,
            musica_id: selectedItem.item_type === "Musica" ? selectedItem.id : null,
            servizio_id: selectedItem.item_type === "Servizio" ? selectedItem.id : null,
            collectible_id: selectedItem.item_type === "Collectible" ? selectedItem.id : null,
            quantita_magazzino: selectedItem.quantita,
            prenotabile: selectedItem.prenotabile === true,
            fuori_catalogo: selectedItem.fuori_catalogo === true,
            prodotto_libero: false,
            ubicazione_scelta: (selectedItem.item_type === "Film" || selectedItem.item_type === "Musica") ? (ubicazioneScelta || null) : null,
            // Aggiungi oggetti completi per supportare badge del supporto
            film: selectedItem.item_type === "Film" ? {
              id: selectedItem.id,
              titolo: selectedItem.titolo,
              supporto: selectedItem.supporto,
              prenotabile: selectedItem.prenotabile === true,
              fuori_catalogo: selectedItem.fuori_catalogo === true
            } : null,
            musica: selectedItem.item_type === "Musica" ? {
              id: selectedItem.id,
              titolo: selectedItem.titolo, 
              supporto: selectedItem.supporto
            } : null,
            collectible: selectedItem.item_type === "Collectible" ? {
              id: selectedItem.id,
              nome: selectedItem.titolo,
              categoria_prodotto: selectedItem.categoria_prodotto
            } : null,
            servizio: selectedItem.item_type === "Servizio" ? {
              id: selectedItem.id,
              titolo_servizio: selectedItem.titolo
            } : null
        };

        setOrdine(prev => ({ ...prev, dettagli: [...prev.dettagli, itemToAdd] }));
        setSelectedItem(null); // Reset selected item
        setQuantita(1); // Reset quantity
    }
  };

  const removeLineItem = (index) => {
    setOrdine(prev => ({ ...prev, dettagli: prev.dettagli.filter((_, i) => i !== index) }));
  };

  const handlePriceChange = (e, index) => {
    const newPrice = parseFloat(e.target.value) || 0;
    const updatedDettagli = ordine.dettagli.map((item, i) => {
        if (i === index) {
            // Per i prodotti liberi, aggiorniamo il prezzo specifico per i prodotti liberi.
            if (item.prodotto_libero) {
                return { ...item, prezzo_unitario_libero: newPrice };
            }
            // Per i prodotti standard, aggiorniamo il prezzo unitario.
            return { ...item, prezzo_unitario: newPrice };
        }
        return item;
    });
    setOrdine(prev => ({ ...prev, dettagli: updatedDettagli }));
  };

  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 0) return;
    if (newQuantity < 1) {
      // Permetti qt=0 solo per film a catalogo (NOT fuori_catalogo) su origini
      // ordine "interne" (NEGOZIO/SITO WEB/ECOMMERCE): ordine su richiesta.
      const item = ordine.dettagli[index];
      const isFilmACatalogo = item?.item_type === 'Film' && item?.fuori_catalogo !== true;
      if (!isFilmACatalogo || !ORIGINI_QT_ZERO_AMMESSE.includes(ordine.origine)) {
        return;
      }
    }
    const updatedDettagli = ordine.dettagli.map((item, i) => {
        if (i === index) {
            return { ...item, quantita: newQuantity };
        }
        return item;
    });
    setOrdine(prev => ({ ...prev, dettagli: updatedDettagli }));
  };

  const toggleEditDetail = (index) => {
    setEditingDetail(editingDetail === index ? null : index);
  };

  const handleDescriptionChange = (index, newDescription) => {
    setOrdine(prev => ({
      ...prev,
      dettagli: prev.dettagli.map((item, i) => 
        i === index ? { 
          ...item, 
          titolo: newDescription,
          descrizione_prodotto_libero: item.prodotto_libero ? newDescription : item.descrizione_prodotto_libero
        } : item
      )
    }));
  };

  const calculateTotal = useCallback(() => {
    let subtotale_imponibile_prodotti = 0;
    let totale_iva_prodotti = 0;
    let costo_prodotti = 0;
    let totale_lordo_prodotti_senza_sconto = 0;

    ordine.dettagli.forEach(item => {
        let prezzo_unitario_effettivo = item.prezzo_unitario;
        let iva_percentuale_effettiva = item.iva_percentuale;
        let costo_unitario_effettivo = item.costo_acquisto_unitario || 0;

        if (item.prodotto_libero) {
            prezzo_unitario_effettivo = item.prezzo_unitario_libero;
            iva_percentuale_effettiva = item.iva_percentuale_libero;
            costo_unitario_effettivo = item.costo_unitario_libero || 0;
        }

        const grossItemTotal = prezzo_unitario_effettivo * item.quantita;
        const ivaRate = iva_percentuale_effettiva / 100;
        
        const netItemTotal = grossItemTotal / (1 + ivaRate);
        const ivaAmount = grossItemTotal - netItemTotal;

        subtotale_imponibile_prodotti += netItemTotal;
        totale_iva_prodotti += ivaAmount;
        costo_prodotti += costo_unitario_effettivo * item.quantita;
        totale_lordo_prodotti_senza_sconto += grossItemTotal;
    });

    // Stessa formula del backend (create_ordine), che ricalcola e salva il totale: prodotti + spedizione,
    // sconto (% su tutto, oppure fisso). Le spese di incasso sono la commissione della banca (costo, non
    // addebitato al cliente) e l'acconto si sottrae solo nel "da incassare".
    let totale = totale_lordo_prodotti_senza_sconto + parseFloat(ordine.spese_trasporto || 0);
    const sconto = parseFloat(ordine.sconto || 0);
    if (sconto > 0) {
        totale -= ordine.tipo_sconto === 'percentuale' ? totale * sconto / 100 : sconto;
    }

    // Costo corriere e costo marketplace arrivano dal backend
    const margine_lordo = totale - costo_prodotti;

    setOrdine(prev => ({
        ...prev,
        subtotale_imponibile: subtotale_imponibile_prodotti,
        totale_iva: totale_iva_prodotti,
        totale_finale: totale,
        costo_prodotti: costo_prodotti,
        costo_marketplace: 0,
        margine_lordo: margine_lordo,
        margine: totale > 0 ? (margine_lordo / totale) * 100 : 0  // mostrato come "Margine %"
    }));
  }, [ordine.dettagli, ordine.spese_trasporto, ordine.spese_incasso_percentuale, ordine.acconto, ordine.sconto, ordine.tipo_sconto]);

  useEffect(() => {
    calculateTotal();
  }, [calculateTotal]); // Dependency on calculateTotal

  // const handleCreateCliente = async () => {
  //   if ((!nuovoCliente.nome || !nuovoCliente.cognome) && !nuovoCliente.denominazione_sociale) {
  //     alert('È obbligatorio inserire Nome e Cognome oppure la Denominazione Sociale.');
  //     return;
  //   }
  //   try {
  //     const response = await createCliente(nuovoCliente);
  //     const newCliente = response.data;
  //     setAllClienti([...allClienti, newCliente]);
  //     setOrdine(prev => ({ ...prev, cliente_id: newCliente.id }));
  //     setShowClienteModal(false);
  //     setNuovoCliente({ nome: '', cognome: '', denominazione_sociale: '' });
  //   } catch (error) {
  //     console.error('Errore nella creazione del cliente:', error);
  //     alert('Errore nella creazione del cliente.');
  //   }
  // };

  const handleCreateFilm = async () => {
    if (!nuovoFilm.titolo) {
      alert('Il titolo è obbligatorio.');
      return;
    }
    try {
      const response = await createFilmAPI(nuovoFilm);
      const newFilm = response.data;
      // No need to update allFilms, as searchResults is dynamic.
      // The newly created film will be available via search if needed.
      setSelectedItem(newFilm); // Use setSelectedItem instead of setSelectedFilm
      setShowFilmModal(false);
      setNuovoFilm({ titolo: '' });
    } catch (error) {
      console.error('Errore nella creazione del film:', error);
      alert('Errore nella creazione del film.');
    }
  };

  // useEffect per ricalcolare le spese di trasporto quando cambiano corriere o origine
  useEffect(() => {
    let calculatedSpeseTrasporto = 0;
    const selectedCorriere = corrieri.find(c => c.id === parseInt(ordine.corriere_id));
    const selectedCliente = allClienti.find(c => c.id === parseInt(ordine.cliente_id));

    console.log('=== DEBUG CALCOLO SPESE SPEDIZIONE ===');
    console.log('Corriere selezionato:', selectedCorriere?.denominazione);
    console.log('Cliente selezionato:', selectedCliente?.nome, selectedCliente?.cognome);
    console.log('Origine ordine:', ordine.origine);

    if (selectedCorriere && selectedCorriere.regole_spedizione && selectedCorriere.regole_spedizione.length > 0) {
      const targetCanale = mapOrigineToCanale(ordine.origine);
      console.log('Canale mappato:', targetCanale);
      console.log('Regole disponibili:', selectedCorriere.regole_spedizione);

      if (targetCanale) {
        // Determina il codice_logica basato sul paese del cliente
        let codiceLogica = 'STANDARD_ITA'; // Default per Italia
        
        if (selectedCliente && selectedCliente.nazione) {
          const nazione = selectedCliente.nazione.toUpperCase().trim();
          if (nazione === 'SVIZZERA' || nazione === 'SWITZERLAND' || nazione === 'CH') {
            codiceLogica = 'SVIZZERA';
          } else if (nazione === 'ITALIA' || nazione === 'ITALY' || nazione === 'IT' || nazione === '') {
            // Per l'Italia, determina la logica basata sul CAP
            if (selectedCliente.cap) {
              const cap = selectedCliente.cap.trim();
              if (cap.length === 5 && cap.match(/^\d{5}$/)) {
                const capPrefix = parseInt(cap.substring(0, 2));
                const MODENA_CAPS = ['41121', '41122', '41123', '41124', '41125', '41126'];
                
                if (MODENA_CAPS.includes(cap)) {
                  codiceLogica = 'MODENA';
                } else if (capPrefix >= 87 && capPrefix <= 89) { // Calabria
                  codiceLogica = 'ISOLE_SUD';
                } else if (capPrefix >= 90 && capPrefix <= 98) { // Sicilia
                  codiceLogica = 'ISOLE_SUD';
                } else if ([7, 8, 9].includes(capPrefix)) { // Sardegna
                  codiceLogica = 'ISOLE_SUD';
                } else {
                  codiceLogica = 'STANDARD_ITA';
                }
              }
            }
          } else {
            // Verifica se è UE o Extra UE
            const paesiUE = ['AUSTRIA', 'BELGIO', 'BULGARIA', 'CIPRO', 'CROAZIA', 'DANIMARCA', 
                            'ESTONIA', 'FINLANDIA', 'FRANCIA', 'GERMANIA', 'GRECIA', 'IRLANDA', 
                            'LETTONIA', 'LITUANIA', 'LUSSEMBURGO', 'MALTA', 'PAESI BASSI', 
                            'POLONIA', 'PORTOGALLO', 'REPUBBLICA CECA', 'ROMANIA', 'SLOVACCHIA', 
                            'SLOVENIA', 'SPAGNA', 'SVEZIA', 'UNGHERIA',
                            'AT', 'BE', 'BG', 'CY', 'HR', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE',
                            'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'CZ', 'RO', 'SK', 'SI', 'ES', 'SE', 'HU'];
            
            if (paesiUE.includes(nazione)) {
              codiceLogica = 'UE';
            } else {
              codiceLogica = 'EXTRA_UE';
            }
          }
        }

        console.log('Codice logica determinato:', codiceLogica);

        // Cerca una regola che corrisponde sia al canale che al codice_logica
        const applicableRule = selectedCorriere.regole_spedizione.find(rule =>
          rule.canale === targetCanale && rule.codice_logica === codiceLogica
        );

        console.log('Regola applicabile trovata:', applicableRule);

        if (applicableRule) {
          calculatedSpeseTrasporto = applicableRule.costo;
          console.log('✅ Costo spedizione calcolato:', calculatedSpeseTrasporto);
        } else {
          console.warn(`❌ Nessuna RegolaSpedizione trovata per corriere ${selectedCorriere.denominazione}, canale ${targetCanale} e codice_logica ${codiceLogica}. Usando costo di default 0.`);
        }
      } else {
         console.warn(`Origine ordine "${ordine.origine}" non mappata a un CanaleSpedizione. Usando costo di default 0.`);
      }
    } else {
      console.warn(`Corriere non selezionato o non ha RegoleSpedizione. Usando costo di default 0.`);
    }

    // <-- AGGIUNGIAMO QUESTA CONDIZIONE
    if (ordine.spese_trasporto !== calculatedSpeseTrasporto) {
      console.log('Aggiornamento spese trasporto da', ordine.spese_trasporto, 'a', calculatedSpeseTrasporto);
      setOrdine(prev => ({ ...prev, spese_trasporto: calculatedSpeseTrasporto }));
    }
    console.log('=== FINE DEBUG ===\n');

  }, [ordine.corriere_id, ordine.origine, ordine.cliente_id, ordine.spese_trasporto, corrieri, allClienti, mapOrigineToCanale]); // Aggiunto ordine.cliente_id e allClienti

  // Form submission
  // Doppio clic su Salva: il secondo invio creerebbe un secondo ordine (stock scaricato due volte).
  // Il ref blocca subito, lo stato serve a disabilitare il pulsante.
  const invioInCorso = useRef(false);
  const [salvataggio, setSalvataggio] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (invioInCorso.current) return;
    if (!ordine.cliente_id || ordine.dettagli.length === 0) {
        alert("Seleziona un cliente e aggiungi almeno un prodotto all'ordine.");
        return;
    }
    invioInCorso.current = true;
    setSalvataggio(true);
    try {
      const ordineToSubmit = {
          cliente_id: ordine.cliente_id,
          corriere_id: ordine.corriere_id ? parseInt(ordine.corriere_id) : null,
          banca_id: ordine.banca_id ? parseInt(ordine.banca_id) : null,
          payment_method: ordine.payment_method || null,
          data_ordine: ordine.data_ordine,
          spese_trasporto: ordine.spese_trasporto,
          spese_incasso_percentuale: ordine.spese_incasso_percentuale,
          subtotale_imponibile: ordine.subtotale_imponibile,
          totale_iva: ordine.totale_iva,
          totale_ordine: ordine.totale_finale,
          costo_prodotti: ordine.costo_prodotti,
          costo_marketplace: ordine.costo_marketplace,
          margine_lordo: ordine.margine_lordo,
          margine: ordine.margine,
          acconto: ordine.acconto,
          sconto: ordine.sconto,
          tipo_sconto: ordine.tipo_sconto,
          origine: ordine.origine,
          numero_ordine_marketplace: ordine.numero_ordine_marketplace,
          invia_email_cliente: EMAIL_FACOLTATIVA.includes(ordine.origine) ? ordine.invia_email_cliente : null,
          note: ordine.note,
          stato: ordine.stato,
          dettagli: ordine.dettagli.map(d => {
              const detail = {
                  quantita: d.quantita,
                  prezzo_unitario: d.prezzo_unitario,
                  iva_percentuale: d.iva_percentuale,
                  costo_acquisto_unitario: d.costo_acquisto_unitario,
                  film_id: d.film_id,
                  musica_id: d.musica_id,
                  servizio_id: d.servizio_id,
                  collectible_id: d.collectible_id, // Aggiunto collectible_id
                  prodotto_libero: d.prodotto_libero || false,
                  descrizione_prodotto_libero: d.descrizione_prodotto_libero || null,
                  prezzo_unitario_libero: d.prezzo_unitario_libero || null,
                  costo_unitario_libero: d.costo_unitario_libero || null,
                  iva_percentuale_libero: d.iva_percentuale_libero || null,
                  ubicazione_scelta: d.ubicazione_scelta || null
              };
              return detail;
          })
      };
      const response = await createOrdine(ordineToSubmit);
      
      // Gestisce la nuova struttura di risposta con prodotti rimossi
      const { ordine: ordineCreato, prodotti_rimossi, has_removed_products } = response.data;
      
      let message = `Ordine #${ordineCreato.id} creato con successo!`;
      
      if (has_removed_products && prodotti_rimossi.length > 0) {
        message += `\n\nATTENZIONE: ${prodotti_rimossi.length} prodotti sono stati rimossi dall'ordine per stock insufficiente:\n`;
        prodotti_rimossi.forEach((prodotto, index) => {
          message += `\n${index + 1}. ${prodotto.nome}`;
          message += `\n   Richiesti: ${prodotto.quantita_richiesta}, Disponibili: ${prodotto.quantita_disponibile}`;
          message += `\n   ${prodotto.motivo}`;
        });
        message += `\n\nL'ordine è stato creato solo con i prodotti disponibili.`;
      }
      
      // Usa un alert più dettagliato o considera una modal per messaggi lunghi
      if (message.length > 200) {
        const shortMessage = `Ordine #${ordineCreato.id} creato con ${has_removed_products ? 'alcune modifiche' : 'successo'}!`;
        const detailsMessage = has_removed_products ? 
          `${prodotti_rimossi.length} prodotti rimossi per stock insufficiente. Controlla i dettagli nell'ordine.` : 
          '';
        alert(shortMessage + (detailsMessage ? '\n' + detailsMessage : ''));
      } else {
        alert(message);
      }
      
      navigate('/ordini');
    } catch (error) {
      console.error('Errore nella creazione dell\'ordine:', error.response || error);
      
      // Gestisce il caso di risposta di errore più specificata
      let errorMessage = 'Errore nella creazione dell\'ordine.';
      if (error.response?.data?.detail) {
        errorMessage += '\n' + error.response.data.detail;
      }
      alert(errorMessage);
      invioInCorso.current = false;
      setSalvataggio(false);
    }
  };

  return (
    <Container>
        <Row className="align-items-center mb-3">
            <Col>
                <h2>Nuovo Ordine</h2>
            </Col>
            <Col xs="auto" className="ms-auto">
                <Button variant="secondary" onClick={() => navigate('/ordini')} className="me-2">
                    Torna alla Lista
                </Button>
                <Button variant="primary" type="submit" form="ordine-form" disabled={salvataggio}>
                    {salvataggio ? 'Salvataggio…' : 'Salva Ordine'}
                </Button>
            </Col>
        </Row>
        
        <Form id="ordine-form" onSubmit={handleSubmit}>
          {isMobile ? (
            // Mobile Accordion Layout
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  📋 Dati Ordine
                </Accordion.Header>
                <Accordion.Body>
                  <h5 className="text-primary mb-4">📋 Informazioni Generali Ordine</h5>
                  <Row className="mb-3">
                    <Form.Group as={Col} xs={12} controlId="formOrigine">
                      <Form.Label>Origine</Form.Label>
                      <Form.Select
                        name="origine"
                        value={ordine.origine}
                        onChange={handleChange}
                      >
                        <option value="">SELEZIONA ORIGINE</option>
                        <option value="NEGOZIO">NEGOZIO</option>
                        <option value="SITO_WEB">SITO WEB</option>
                        <option value="IBS">IBS</option>
                        <option value="FELTRINELLI">FELTRINELLI</option>
                        <option value="RIVERSAMENTI">RIVERSAMENTI</option>
                        <option value="ALTRO">ALTRO</option>
                      </Form.Select>
                    </Form.Group>

                    { (ordine.origine === 'IBS' || ordine.origine === 'FELTRINELLI') && (
                      <Form.Group as={Col} xs={12} className="mt-3" controlId="formNumeroOrdineMarketplace">
                        <Form.Label>Numero Ordine Marketplace</Form.Label>
                        <Form.Control
                          type="text"
                          name="numero_ordine_marketplace"
                          value={ordine.numero_ordine_marketplace}
                          onChange={handleChange}
                          placeholder="Inserisci numero ordine marketplace"
                        />
                      </Form.Group>
                    )}

                    {EMAIL_FACOLTATIVA.includes(ordine.origine) && (
                      <Form.Group as={Col} xs={12} className="mt-3" controlId="formEmailClienteMobile">
                        {casellaEmailCliente}
                      </Form.Group>
                    )}

                    <Form.Group as={Col} xs={12} className="mt-3" controlId="formClienteMobile">
                      <Form.Label>Cliente</Form.Label>
                      <InputGroup>
                        <Typeahead
                          id="cliente-typeahead-mobile"
                          labelKey={option => {
                            const namePart = [option.nome, option.cognome].filter(Boolean).join(' ');
                            const companyPart = option.denominazione_sociale ? ` (${option.denominazione_sociale})` : '';
                            return (namePart + companyPart).trim() || 'Cliente Sconosciuto';
                          }}
                          onChange={handleClienteChange}
                          options={allClienti}
                          placeholder="Cerca per cognome..."
                          selected={ordine.cliente_id ? allClienti.filter(c => c.id === ordine.cliente_id) : []}
                          filterBy={['nome', 'cognome', 'denominazione_sociale']}
                        />
                        <Button variant="outline-secondary" onClick={() => navigate('/clienti/new', { state: { fromOrder: true } })}>
                          Nuovo
                        </Button>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group as={Col} xs={12} className="mt-3" controlId="formDataOrdineMobile">
                      <Form.Label>Data Ordine</Form.Label>
                      <Form.Control type="date" name="data_ordine" value={ordine.data_ordine} onChange={handleOrdineChange} />
                    </Form.Group>

                    <Form.Group as={Col} xs={12} className="mt-3" controlId="formStatoMobile">
                      <Form.Label>Stato Ordine</Form.Label>
                      <Form.Select name="stato" value={ordine.stato} onChange={handleOrdineChange}>
                        <option value="Da Evadere">Da Evadere</option>
                        <option value="Confermato">Confermato</option>
                        <option value="Parz.Concluso">Parz.Concluso</option>
                        <option value="Concluso">Concluso</option>
                        <option value="Annullato">Annullato</option>
                        <option value="Rimborsato">Rimborsato</option>
                        <option value="Non Ritirato - Contrassegno">Non Ritirato - Contrassegno</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group as={Col} xs={12} className="mt-3" controlId="formNoteOrdineMobile">
                      <Form.Label>Note Ordine</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="note"
                        value={ordine.note || ''}
                        onChange={handleOrdineChange}
                        placeholder="Inserisci eventuali note sull'ordine..."
                      />
                    </Form.Group>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  📦 Prodotti {ordine.dettagli && ordine.dettagli.length > 0 && (
                    <Badge bg="primary" className="ms-2">{ordine.dettagli.length}</Badge>
                  )}
                </Accordion.Header>
                <Accordion.Body>
                  <h5 className="text-success mb-4">📦 Gestione Prodotti e Servizi</h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="radio"
                      label="Prodotto Esistente"
                      name="productType"
                      id="productTypeExistingMobile"
                      checked={!isCustomProductMode}
                      onChange={() => setIsCustomProductMode(false)}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      label="Prodotto Personalizzato"
                      name="productType"
                      id="productTypeCustomMobile"
                      checked={isCustomProductMode}
                      onChange={() => setIsCustomProductMode(true)}
                    />
                  </Form.Group>

                  {!isCustomProductMode ? (
                    <div className="mb-3">
                      <Form.Group controlId="formItemSearchMobile" className="mb-3">
                        <Form.Label>Prodotto/Servizio</Form.Label>
                        <InputGroup>
                          <Typeahead
                            id="item-search-typeahead-mobile"
                            labelKey={(option) => {
                              let label = '';
                              if (option.item_type === "Film" || option.item_type === "Musica") {
                                label = `${option.titolo} - ${option.supporto || option.item_type}${option.sku ? ` - SKU: ${option.sku}` : ''} - Qt: ${option.quantita}`;
                              } else if (option.item_type === "Servizio") {
                                label = `${option.titolo} - Servizio${option.sku ? ` - SKU: ${option.sku}` : ''} - Prezzo: ${option.prezzo}`;
                              } else {
                                label = option.titolo; // Fallback
                              }
                              return label;
                            }}
                            options={searchResults}
                            onInputChange={handleSearch}
                            onChange={(selected) => {
                              if (selected.length > 0) {
                                const selectedOption = selected[0];
                                const itemForSelection = {
                                  ...selectedOption,
                                  prezzo_negozio: selectedOption.prezzo,
                                  iva: selectedOption.iva || 22
                                };
                                if (selectedOption.item_type === "Servizio") {
                                  itemForSelection.quantita = 999999;
                                }
                                setSelectedItem(itemForSelection);
                              } else {
                                setSelectedItem(null);
                              }
                            }}
                            placeholder="Cerca titolo, SKU o barcode..."
                            selected={selectedItem ? [selectedItem] : []}
                            filterBy={() => true}
                            renderMenuItemChildren={(option) => {
                              const isOutOfStock = (option.item_type === "Film" || option.item_type === "Musica") && option.quantita === 0 && !(option.item_type === "Film" && option.fuori_catalogo !== true);
                              let label = '';
                              if (option.item_type === "Film" || option.item_type === "Musica") {
                                label = `${option.titolo} - ${option.supporto || option.item_type}${option.sku ? ` - SKU: ${option.sku}` : ''} - Qt: ${option.quantita}`;
                              } else if (option.item_type === "Servizio") {
                                label = `${option.titolo} - Servizio${option.sku ? ` - SKU: ${option.sku}` : ''} - Prezzo: ${option.prezzo}`;
                              } else {
                                label = option.titolo;
                              }
                              return (
                                <div style={{ color: isOutOfStock ? 'red' : 'black' }}>
                                  {label}
                                </div>
                              );
                            }}
                          />
                          <Button variant="outline-secondary" onClick={() => setShowFilmModal(true)}>
                            Nuovo
                          </Button>
                        </InputGroup>
                        {selectedItem && <Form.Text className="text-muted">Disponibile: {selectedItem.quantita}</Form.Text>}
                      </Form.Group>

                      {ubicazioniProdotto.length > 1 && (
                        <Form.Group controlId="formUbicazioneMobile" className="mb-3">
                          <Form.Label>Preleva da ubicazione</Form.Label>
                          <Form.Select value={ubicazioneScelta} onChange={(e) => setUbicazioneScelta(e.target.value)}>
                            <option value="">Seleziona ubicazione...</option>
                            {ubicazioniProdotto.map(r => (
                              <option key={r.ubicazione} value={r.ubicazione}>{r.ubicazione} (Qt: {r.quantita})</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}

                      <Form.Group controlId="formQuantitaMobile" className="mb-3">
                        <Form.Label>Quantità</Form.Label>
                        <Form.Control
                          type="number"
                          value={quantita}
                          onChange={(e) => setQuantita(parseInt(e.target.value))}
                          min="0"
                          max={selectedItem ? selectedItem.quantita : 9999}
                        />
                      </Form.Group>

                      <Button variant="secondary" onClick={addLineItem} className="w-100 mb-3">
                        Aggiungi Prodotto
                      </Button>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <Form.Group controlId="formCustomProductDescriptionMobile" className="mb-3">
                        <Form.Label>Descrizione Prodotto</Form.Label>
                        <Form.Control
                          type="text"
                          value={customProductDescription}
                          onChange={(e) => setCustomProductDescription(e.target.value)}
                          placeholder="Descrizione prodotto personalizzato"
                        />
                      </Form.Group>

                      <Row>
                        <Col xs={6}>
                          <Form.Group controlId="formCustomProductPriceMobile" className="mb-3">
                            <Form.Label>Prezzo Unitario</Form.Label>
                            <Form.Control
                              type="number"
                              value={customProductPrice}
                              onChange={(e) => setCustomProductPrice(parseFloat(e.target.value))}
                              step="0.01"
                              min="0"
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6}>
                          <Form.Group controlId="formCustomProductCostMobile" className="mb-3">
                            <Form.Label>Costo Unitario</Form.Label>
                            <Form.Control
                              type="number"
                              value={customProductCost}
                              onChange={(e) => setCustomProductCost(parseFloat(e.target.value))}
                              step="0.01"
                              min="0"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col xs={6}>
                          <Form.Group controlId="formCustomProductIvaMobile" className="mb-3">
                            <Form.Label>IVA %</Form.Label>
                            <Form.Control
                              type="number"
                              value={customProductIva}
                              onChange={(e) => setCustomProductIva(parseFloat(e.target.value))}
                              step="0.01"
                              min="0"
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6}>
                          <Form.Group controlId="formQuantitaCustomMobile" className="mb-3">
                            <Form.Label>Quantità</Form.Label>
                            <Form.Control
                              type="number"
                              value={quantita}
                              onChange={(e) => setQuantita(parseInt(e.target.value))}
                              min="1"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Button variant="secondary" onClick={addLineItem} className="w-100 mb-3">
                        Aggiungi Prodotto Personalizzato
                      </Button>
                    </div>
                  )}

                  {/* Mobile Product Cards */}
                  {ordine.dettagli.map((item, index) => {
                    const prezzo_effettivo = item.prodotto_libero ? item.prezzo_unitario_libero : item.prezzo_unitario;
                    const sub = prezzo_effettivo * item.quantita;
                    const costo = item.costo_unitario_libero || item.costo_acquisto_unitario || 0;
                    const margineLordo = sub - (costo * item.quantita);
                    const marginePercentuale = sub > 0 ? (margineLordo / sub) * 100 : 0;
                    const stockClass = (item.item_type === "Film" || item.item_type === "Musica") && item.quantita_magazzino !== undefined && item.quantita_magazzino === 0;

                    let descrizione = item.prodotto_libero ? item.descrizione_prodotto_libero : item.titolo;
                    let supporto = '';
                    if (item.film && item.film.supporto) {
                      supporto = item.film.supporto;
                    } else if (item.musica && item.musica.supporto) {
                      supporto = item.musica.supporto;
                    } else if (item.item_type === "Servizio") {
                      supporto = "Servizio";
                    } else if (item.prodotto_libero) {
                      supporto = "Prodotto Libero";
                    }

                    return (
                      <div key={index} className="mobile-product-card">
                        <div className="product-header">
                          <div className="product-title">
                            {descrizione}
                            {supporto && <><br/><Badge bg="secondary" size="sm">{supporto}</Badge></>}
                            {stockClass && <><br/><Badge bg="danger" size="sm">Esaurito</Badge></>}
                          </div>
                          <div className="product-actions">
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              onClick={() => toggleEditDetail(index)}
                              title="Modifica"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => removeLineItem(index)} 
                              title="Rimuovi"
                            >
                              🗑️
                            </Button>
                          </div>
                        </div>
                        
                        <div className="product-details">
                          <div><small><strong>Quantità:</strong> {item.quantita}</small></div>
                          <div><small><strong>Prezzo:</strong> €{prezzo_effettivo.toFixed(2)}</small></div>
                          <div><small><strong>IVA:</strong> {(item.iva_percentuale_libero || item.iva_percentuale)}%</small></div>
                          <div><small><strong>Subtotale:</strong> €{sub.toFixed(2)}</small></div>
                          <div><small><strong>Costo:</strong> €{costo.toFixed(2)}</small></div>
                          <div><small><strong>Margine:</strong> €{margineLordo.toFixed(2)} ({marginePercentuale.toFixed(1)}%)</small></div>
                        </div>
                      </div>
                    );
                  })}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  💰 Costi e Pagamento {ordine.totale_finale > 0 && (
                    <Badge bg="success" className="ms-2">€{ordine.totale_finale.toFixed(2)}</Badge>
                  )}
                </Accordion.Header>
                <Accordion.Body>
                  <h5 className="text-warning mb-4">💰 Configurazione Costi e Pagamenti</h5>
                  
                  <Form.Group controlId="formCorriereMobile" className="mb-3">
                    <Form.Label>Corriere</Form.Label>
                    <Form.Select name="corriere_id" value={ordine.corriere_id} onChange={handleOrdineChange}>
                        <option value="">Seleziona un tipo di spedizione...</option>
                        {corrieri.filter(c => c.tipo_spedizione).map(c => 
                          <option key={c.id} value={c.id}>{c.tipo_spedizione}</option>
                        )}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group controlId="formMetodoPagamentoMobile" className="mb-3">
                    <Form.Label>Metodo di pagamento <span className="text-muted">(come ha pagato il cliente)</span></Form.Label>
                    <Form.Select name="payment_method" value={ordine.payment_method || ''} onChange={handlePaymentMethodChange}>
                      <option value="">Seleziona…</option>
                      {METODI_PAGAMENTO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group controlId="formBancaMobile" className="mb-3">
                    <Form.Label>Banca <span className="text-muted">(rapporto di incasso: dove sono arrivati i soldi)</span></Form.Label>
                    <Form.Select name="banca_id" value={ordine.banca_id || ''} onChange={handleBancaChange}>
                      <option value="">Seleziona…</option>
                      {banche.map(b => <option key={b.id} value={b.id}>{b.denominazione}</option>)}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group controlId="formSpeseTrasportoMobile" className="mb-3">
                    <Form.Label>Spese Trasporto</Form.Label>
                    <Form.Control type="number" value={(ordine.spese_trasporto ?? 0).toFixed(2)} readOnly disabled />
                  </Form.Group>

                  <Form.Group controlId="formSpeseIncassoMobile" className="mb-3">
                    <Form.Label>Spese Incasso %</Form.Label>
                    <Form.Control type="number" name="spese_incasso_percentuale" value={ordine.spese_incasso_percentuale} onChange={handleOrdineChange} step="0.1" />
                  </Form.Group>

                  <Form.Group controlId="formScontoMobile" className="mb-3">
                    <Form.Label>Sconto</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        name="sconto"
                        value={ordine.sconto}
                        onChange={handleOrdineChange}
                        step="0.01"
                      />
                      <Form.Select
                        name="tipo_sconto"
                        value={ordine.tipo_sconto}
                        onChange={handleChange}
                      >
                        <option value="fisso">€</option>
                        <option value="percentuale">%</option>
                      </Form.Select>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group controlId="formAccontoMobile" className="mb-3">
                    <Form.Label>Acconto</Form.Label>
                    <Form.Control type="number" name="acconto" value={ordine.acconto} onChange={handleOrdineChange} step="0.01" />
                  </Form.Group>

                  <hr />
                  <div className="text-end">
                      <p>Subtotale Imponibile: <strong>€{(ordine.subtotale_imponibile ?? 0).toFixed(2)}</strong></p>
                      <p>Totale IVA: <strong>€{(ordine.totale_iva ?? 0).toFixed(2)}</strong></p>
                      <h4>Totale Finale: <strong>€{(ordine.totale_finale ?? 0).toFixed(2)}</strong></h4>
                      {parseFloat(ordine.acconto || 0) > 0 && (
                        <p className="text-muted mb-0">Acconto €{parseFloat(ordine.acconto).toFixed(2)} — da incassare €{((ordine.totale_finale ?? 0) - parseFloat(ordine.acconto)).toFixed(2)}</p>
                      )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  📊 Marginalità {ordine.margine_lordo > 0 && (
                    <Badge bg="info" className="ms-2">€{ordine.margine_lordo.toFixed(2)}</Badge>
                  )}
                </Accordion.Header>
                <Accordion.Body>
                  <h5 className="text-info mb-4">📊 Analisi Marginalità e Profitti</h5>
                  <div className="mb-3">
                    <p>Costo Prodotti: <strong>€{(ordine.costo_prodotti ?? 0).toFixed(2)}</strong></p>
                    <p>Costo Marketplace: <strong>€{(ordine.costo_marketplace ?? 0).toFixed(2)}</strong></p>
                    <p>Costo Corrieri: <strong>€{(ordine.costo_reale_corriere ?? 0).toFixed(2)}</strong></p>
                  </div>
                  <hr />
                  <div className="text-end">
                    <h4>Margine Lordo: <strong>€{(ordine.margine_lordo ?? 0).toFixed(2)}</strong></h4>
                    <h4>Margine %: <strong>{(ordine.margine ?? 0).toFixed(2)}%</strong></h4>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          ) : (
            // Desktop Tab Layout
            <Tab.Container defaultActiveKey="intestazione">
              <Card>
                <Card.Header className="bg-light">
                  <Nav variant="tabs" className="card-header-tabs">
                    <Nav.Item>
                      <Nav.Link eventKey="intestazione" className="d-flex align-items-center gap-2">
                        📋 <span>Dati Ordine</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="prodotti" className="d-flex align-items-center gap-2">
                        📦 <span>Prodotti</span>
                        {ordine.dettagli && ordine.dettagli.length > 0 && 
                          <span className="badge bg-primary rounded-pill">{ordine.dettagli.length}</span>
                        }
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="costi" className="d-flex align-items-center gap-2">
                        💰 <span>Costi e Pagamento</span>
                        {ordine.totale_finale > 0 && 
                          <span className="badge bg-success rounded-pill">€ {(ordine.totale_finale).toFixed(2)}</span>
                        }
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="margini" className="d-flex align-items-center gap-2">
                        📊 <span>Marginalità</span>
                        {ordine.margine_lordo > 0 && 
                          <span className="badge bg-info rounded-pill">€ {(ordine.margine_lordo).toFixed(2)}</span>
                        }
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Header>
                <Card.Body className="p-4">
                  <Tab.Content>
                    <Tab.Pane eventKey="intestazione" className="mt-3">
                      <h5 className="text-primary mb-4">📋 Informazioni Generali Ordine</h5>
            <Row className="mb-3">
          <Form.Group as={Col} md={6} controlId="formOrigine">
            <Form.Label>Origine</Form.Label>
            <Form.Select
              name="origine"
              value={ordine.origine}
              onChange={handleChange}
            >
              <option value="">SELEZIONA ORIGINE</option>
              <option value="NEGOZIO">NEGOZIO</option>
              <option value="SITO_WEB">SITO WEB</option>
              <option value="IBS">IBS</option>
              <option value="FELTRINELLI">FELTRINELLI</option>
              <option value="RIVERSAMENTI">RIVERSAMENTI</option>
              <option value="ALTRO">ALTRO</option>
            </Form.Select>
          </Form.Group>

          { (ordine.origine === 'IBS' || ordine.origine === 'FELTRINELLI') && (
            <Form.Group as={Col} md={6} controlId="formNumeroOrdineMarketplace">
              <Form.Label>Numero Ordine Marketplace</Form.Label>
              <Form.Control
                type="text"
                name="numero_ordine_marketplace"
                value={ordine.numero_ordine_marketplace}
                onChange={handleChange}
                placeholder="Inserisci numero ordine marketplace"
              />
            </Form.Group>
          )}

          {EMAIL_FACOLTATIVA.includes(ordine.origine) && (
            <Form.Group as={Col} md={6} className="d-flex align-items-end pb-2" controlId="formEmailCliente">
              {casellaEmailCliente}
            </Form.Group>
          )}

          <Form.Group as={Col} md={6} controlId="formCliente">
            <Form.Label>Cliente</Form.Label>
            <InputGroup>
              <Typeahead
                id="cliente-typeahead"
                labelKey={option => {
    const namePart = [option.nome, option.cognome].filter(Boolean).join(' ');
    const companyPart = option.denominazione_sociale ? ` (${option.denominazione_sociale})` : '';
    return (namePart + companyPart).trim() || 'Cliente Sconosciuto';
  }}
                onChange={handleClienteChange}
                options={allClienti}
                placeholder="Cerca per cognome..."
                selected={ordine.cliente_id ? allClienti.filter(c => c.id === ordine.cliente_id) : []}
                filterBy={['nome', 'cognome', 'denominazione_sociale']}
              />
              <Button variant="outline-secondary" onClick={() => navigate('/clienti/new', { state: { fromOrder: true } })}>
                Nuovo
              </Button>
            </InputGroup>
          </Form.Group>
              <Form.Group as={Col} md={4} controlId="formDataOrdine">
                <Form.Label>Data Ordine</Form.Label>
                <Form.Control type="date" name="data_ordine" value={ordine.data_ordine} onChange={handleOrdineChange} />
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="formStato">
                <Form.Label>Stato Ordine</Form.Label>
                <Form.Select name="stato" value={ordine.stato} onChange={handleOrdineChange}>
                  <option value="Da Evadere">Da Evadere</option>
                  <option value="Confermato">Confermato</option>
                  <option value="Parz.Concluso">Parz.Concluso</option>
                  <option value="Concluso">Concluso</option>
                  <option value="Annullato">Annullato</option>
                        <option value="Rimborsato">Rimborsato</option>
                        <option value="Non Ritirato - Contrassegno">Non Ritirato - Contrassegno</option>
                </Form.Select>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} md={12} controlId="formNoteOrdine">
                <Form.Label>Note Ordine</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="note"
                  value={ordine.note || ''}
                  onChange={handleOrdineChange}
                  placeholder="Inserisci eventuali note sull'ordine..."
                />
              </Form.Group>
            </Row>
                  </Tab.Pane>

                  <Tab.Pane eventKey="prodotti" className="mt-3">
                    <h5 className="text-success mb-4">📦 Gestione Prodotti e Servizi</h5>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    inline
                    type="radio"
                    label="Prodotto Esistente"
                    name="productType"
                    id="productTypeExisting"
                    checked={!isCustomProductMode}
                    onChange={() => setIsCustomProductMode(false)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Prodotto Personalizzato"
                    name="productType"
                    id="productTypeCustom"
                    checked={isCustomProductMode}
                    onChange={() => setIsCustomProductMode(true)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {!isCustomProductMode ? (
              <Row className="align-items-end mb-3">
                <Form.Group as={Col} md={7} controlId="formItemSearch">
                  <Form.Label>Aggiungi Prodotto/Servizio</Form.Label>
                  <InputGroup>
                      <Typeahead
                        id="item-search-typeahead"
                        labelKey={(option) => {
                          let label = '';
                          if (option.item_type === "Film" || option.item_type === "Musica") {
                            label = `${option.titolo} - ${option.supporto || option.item_type}${option.sku ? ` - SKU: ${option.sku}` : ''} - Qt: ${option.quantita}`;
                          } else if (option.item_type === "Servizio") {
                            label = `${option.titolo} - Servizio${option.sku ? ` - SKU: ${option.sku}` : ''} - Prezzo: ${option.prezzo}`;
                          } else {
                            label = option.titolo; // Fallback
                          }
                          return label;
                        }}
                        options={searchResults}
                        onInputChange={handleSearch}
                        onChange={(selected) => {
                          if (selected.length > 0) {
                            const selectedItem = selected[0];
                            const itemForSelection = {
                                ...selectedItem,
                                prezzo_negozio: selectedItem.prezzo,
                                iva: selectedItem.iva || 22
                            };
                            if (selectedItem.item_type === "Servizio") {
                                itemForSelection.quantita = 999999;
                            }
                            setSelectedItem(itemForSelection);
                          } else {
                            setSelectedItem(null);
                          }
                        }}
                        placeholder="Cerca o seleziona un prodotto/servizio..."
                        selected={selectedItem ? [selectedItem] : []}
                        filterBy={() => true}
                        renderMenuItemChildren={(option, props) => {
                          const isOutOfStock = (option.item_type === "Film" || option.item_type === "Musica") && option.quantita === 0 && !(option.item_type === "Film" && option.fuori_catalogo !== true);
                          let label = '';
                          if (option.item_type === "Film" || option.item_type === "Musica") {
                            label = `${option.titolo} - ${option.supporto || option.item_type}${option.sku ? ` - SKU: ${option.sku}` : ''} - Qt: ${option.quantita}`;
                          } else if (option.item_type === "Servizio") {
                            label = `${option.titolo} - Servizio${option.sku ? ` - SKU: ${option.sku}` : ''} - Prezzo: ${option.prezzo}`;
                          } else {
                            label = option.titolo; // Fallback
                          }

                          return (
                            <div style={{ color: isOutOfStock ? 'red' : 'black' }}>
                              {label}
                            </div>
                          );
                        }}
                      />
                    <Button variant="outline-secondary" onClick={() => setShowFilmModal(true)}>
                      Nuovo
                    </Button>
                  </InputGroup>
                  {selectedItem && <Form.Text className="text-muted">Disponibile: {selectedItem.quantita}</Form.Text>}
                </Form.Group>
                <Form.Group as={Col} md={3} controlId="formQuantita">
                  <Form.Label>Quantità</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={quantita} 
                    onChange={(e) => setQuantita(parseInt(e.target.value))} 
                    min="0" 
                    max={selectedItem ? selectedItem.quantita : 9999}
                  />
                </Form.Group>
                <Col md={2} className="d-flex align-items-end">
                  <Button variant="secondary" onClick={addLineItem} className="w-100">Aggiungi</Button>
                </Col>
                {ubicazioniProdotto.length > 1 && (
                  <Form.Group as={Col} md={7} controlId="formUbicazione" className="mt-2">
                    <Form.Label>Preleva da ubicazione</Form.Label>
                    <Form.Select value={ubicazioneScelta} onChange={(e) => setUbicazioneScelta(e.target.value)}>
                      <option value="">Seleziona ubicazione...</option>
                      {ubicazioniProdotto.map(r => (
                        <option key={r.ubicazione} value={r.ubicazione}>{r.ubicazione} (Qt: {r.quantita})</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
              </Row>
            ) : (
              <Row className="align-items-end mb-3">
                <Form.Group as={Col} md={4} controlId="formCustomProductDescription">
                  <Form.Label>Descrizione Prodotto</Form.Label>
                  <Form.Control
                    type="text"
                    value={customProductDescription}
                    onChange={(e) => setCustomProductDescription(e.target.value)}
                    placeholder="Descrizione prodotto personalizzato"
                  />
                </Form.Group>
                <Form.Group as={Col} md={2} controlId="formCustomProductPrice">
                  <Form.Label>Prezzo Unitario</Form.Label>
                  <Form.Control
                    type="number"
                    value={customProductPrice}
                    onChange={(e) => setCustomProductPrice(parseFloat(e.target.value))}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
                <Form.Group as={Col} md={2} controlId="formCustomProductCost">
                  <Form.Label>Costo Unitario</Form.Label>
                  <Form.Control
                    type="number"
                    value={customProductCost}
                    onChange={(e) => setCustomProductCost(parseFloat(e.target.value))}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
                <Form.Group as={Col} md={2} controlId="formCustomProductIva">
                  <Form.Label>IVA %</Form.Label>
                  <Form.Control
                    type="number"
                    value={customProductIva}
                    onChange={(e) => setCustomProductIva(parseFloat(e.target.value))}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
                <Form.Group as={Col} md={1} controlId="formQuantitaCustom">
                  <Form.Label>Quantità</Form.Label>
                  <Form.Control
                    type="number"
                    value={quantita}
                    onChange={(e) => setQuantita(parseInt(e.target.value))}
                    min="1"
                  />
                </Form.Group>
                <Col md={1} className="d-flex align-items-end">
                  <Button variant="secondary" onClick={addLineItem} className="w-100">Aggiungi</Button>
                </Col>
              </Row>
            )}

            {!isMobile && (
              <Table striped bordered hover responsive className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Prodotto / Descrizione</th>
                    <th style={{width: '80px'}}>Q.tà</th>
                    <th style={{width: '120px'}}>Prezzo Unit.</th>
                    <th style={{width: '80px'}}>IVA %</th>
                    <th style={{width: '120px'}}>Subtotale</th>
                    <th style={{width: '100px'}}>Costo</th>
                    <th style={{width: '120px'}}>Margine Lordo</th>
                    <th style={{width: '90px'}}>Margine %</th>
                    <th style={{width: '80px'}}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                    {ordine.dettagli.map((item, index) => {
                        const prezzo_effettivo = item.prodotto_libero ? item.prezzo_unitario_libero : item.prezzo_unitario;
                        const sub = prezzo_effettivo * item.quantita;
                        const costo = item.costo_unitario_libero || item.costo_acquisto_unitario || 0;
                        const margineLordo = sub - (costo * item.quantita);
                        const marginePercentuale = sub > 0 ? (margineLordo / sub) * 100 : 0;
                        const stockClass = (item.item_type === "Film" || item.item_type === "Musica") && item.quantita_magazzino !== undefined && item.quantita_magazzino === 0 ? 'table-danger' : '';
                        
                        // Determina descrizione e supporto
                        let descrizione = item.prodotto_libero ? item.descrizione_prodotto_libero : item.titolo;
                        let supporto = '';
                        if (item.film && item.film.supporto) {
                          supporto = item.film.supporto;
                        } else if (item.musica && item.musica.supporto) {
                          supporto = item.musica.supporto;
                        } else if (item.item_type === "Servizio") {
                          supporto = "Servizio";
                        } else if (item.prodotto_libero) {
                          supporto = "Prodotto Libero";
                        }
                        
                        const isEditing = editingDetail === index;
                        
                        return (
                          <tr key={index} className={stockClass}>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  type="text"
                                  value={descrizione}
                                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                  size="sm"
                                />
                              ) : (
                                <div>
                                  <strong>{descrizione}</strong>
                                  {supporto && <><br/><Badge bg="secondary" className="mt-1">{supporto}</Badge></>}
                                  {stockClass && <><br/><Badge bg="danger" className="mt-1">Esaurito</Badge></>}
                                </div>
                              )}
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={item.quantita}
                                onChange={(e) => handleQuantityChange(index, parseInt(e.target.value))}
                                min="1"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                  type="number"
                                  value={prezzo_effettivo}
                                  onChange={(e) => handlePriceChange(e, index)}
                                  step="0.01"
                                  size="sm"
                              />
                            </td>
                            <td className="text-center">{(item.iva_percentuale_libero || item.iva_percentuale)}%</td>
                            <td className="text-end"><strong>€ {sub.toFixed(2)}</strong></td>
                            <td className="text-end">€ {costo.toFixed(2)}</td>
                            <td className="text-end">€ {margineLordo.toFixed(2)}</td>
                            <td className="text-end">{marginePercentuale.toFixed(2)}%</td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button 
                                  variant={isEditing ? "success" : "outline-primary"} 
                                  size="sm" 
                                  onClick={() => toggleEditDetail(index)}
                                  title={isEditing ? "Conferma" : "Modifica descrizione"}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  onClick={() => removeLineItem(index)} 
                                  title="Rimuovi"
                                >
                                  🗑️
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                    })}
                </tbody>
              </Table>
            )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="costi" className="mt-3">
                    <h5 className="text-warning mb-4">💰 Configurazione Costi e Pagamenti</h5>
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="formCorriere">
                <Form.Label>Corriere</Form.Label>
                <Form.Select name="corriere_id" value={ordine.corriere_id} onChange={handleOrdineChange}>
                    <option value="">Seleziona un tipo di spedizione...</option>
                    {corrieri.filter(c => c.tipo_spedizione).map(c => <option key={c.id} value={c.id}>{c.tipo_spedizione}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="formMetodoPagamento">
                <Form.Label>Metodo di pagamento <span className="text-muted">(come ha pagato il cliente)</span></Form.Label>
                <Form.Select name="payment_method" value={ordine.payment_method || ''} onChange={handlePaymentMethodChange}>
                  <option value="">Seleziona…</option>
                  {METODI_PAGAMENTO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="formBanca">
                <Form.Label>Banca <span className="text-muted">(rapporto di incasso: dove sono arrivati i soldi)</span></Form.Label>
                <Form.Select name="banca_id" value={ordine.banca_id || ''} onChange={handleBancaChange}>
                  <option value="">Seleziona…</option>
                  {banche.map(b => <option key={b.id} value={b.id}>{b.denominazione}</option>)}
                </Form.Select>
              </Form.Group>
            </Row>
            <Row className="align-items-end">
              <Form.Group as={Col} md={4} controlId="formSpeseTrasporto">
                <Form.Label>Spese Trasporto</Form.Label>
                <Form.Control type="number" value={(ordine.spese_trasporto ?? 0).toFixed(2)} readOnly disabled />
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="formSpeseIncasso">
                <Form.Label>Spese Incasso %</Form.Label>
                <Form.Control type="number" name="spese_incasso_percentuale" value={ordine.spese_incasso_percentuale} onChange={handleOrdineChange} step="0.1" />
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="formSconto">
                <Form.Label>Sconto</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    name="sconto"
                    value={ordine.sconto}
                    onChange={handleOrdineChange}
                    step="0.01"
                  />
                  <Form.Select
                    name="tipo_sconto"
                    value={ordine.tipo_sconto}
                    onChange={handleChange}
                    style={{ width: 'auto' }}
                  >
                    <option value="fisso">€</option>
                    <option value="percentuale">%</option>
                  </Form.Select>
                </InputGroup>
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="formAcconto">
                <Form.Label>Acconto</Form.Label>
                <Form.Control type="number" name="acconto" value={ordine.acconto} onChange={handleOrdineChange} step="0.01" />
              </Form.Group>
            </Row>
            <hr />
            <div className="text-end">
                <p>Subtotale Imponibile: € {(ordine.subtotale_imponibile ?? 0).toFixed(2)}</p>
                <p>Totale IVA: € {(ordine.totale_iva ?? 0).toFixed(2)}</p>
                <h3>Totale Finale: € {(ordine.totale_finale ?? 0).toFixed(2)}</h3>
            </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="margini" className="mt-3">
                    <h5 className="text-info mb-4">📊 Analisi Marginalità e Profitti</h5>
            <Row>
              <Col md={6}>
                <p>Costo Prodotti: € {(ordine.costo_prodotti ?? 0).toFixed(2)}</p>
                <p>Costo Marketplace: € {(ordine.costo_marketplace ?? 0).toFixed(2)}</p>
                <p>Costo Corrieri: € {(ordine.costo_reale_corriere ?? 0).toFixed(2)}</p>
              </Col>
              <Col md={6} className="text-end">
                <h4>Margine Lordo: € {(ordine.margine_lordo ?? 0).toFixed(2)}</h4>
                <h4>Margine %: {(ordine.margine ?? 0).toFixed(2)}%</h4>
              </Col>
            </Row>
                  </Tab.Pane>
                </Tab.Content>
              </Card.Body>
            </Card>
          </Tab.Container>
          )}
        </Form>

      {/* Modal per Nuovo Cliente (rimosso) */}

      <Modal show={showFilmModal} onHide={() => setShowFilmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Nuovo Film</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Titolo</Form.Label>
              <Form.Control 
                type="text" 
                value={nuovoFilm.titolo} 
                onChange={(e) => setNuovoFilm({...nuovoFilm, titolo: e.target.value})} 
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilmModal(false)}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleCreateFilm}>
            Salva Film
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default NuovoOrdine;
